import React, { createContext, useContext, useEffect } from 'react';

import { IBaseProps } from '@rbi-ctg/frontend';
import useEffectOnUpdates from 'hooks/use-effect-on-updates';
import { usePrevious } from 'hooks/use-previous';
import { actions, useAppDispatch } from 'state/global-state';

import {
  IUseLoyalty,
  IUseLoyaltyNotifications,
  IUseLoyaltyOffers,
  IUseLoyaltyRewards,
} from './hooks/types';
import { useLoyalty } from './hooks/use-loyalty';
import { useLoyaltyNotifications } from './hooks/use-loyalty-notifications';
import { useLoyaltyOffers } from './hooks/use-loyalty-offers';
import { useLoyaltyRewards } from './hooks/use-loyalty-rewards';
import { InRestaurantRedemptionProvider } from './in-restaurant-redemption';
import { ThLoyaltyProvider } from './th-loyalty-context';

// Leaving as an interface to be extended in future PR
export interface ILoyaltyContext
  extends IUseLoyalty,
    IUseLoyaltyRewards,
    IUseLoyaltyOffers,
    IUseLoyaltyNotifications {}

export const LoyaltyContext = createContext({} as ILoyaltyContext);
export const useLoyaltyContext = () => useContext(LoyaltyContext);

export const LoyaltyProvider = (props: IBaseProps) => {
  const {
    claimPointsEnabled,
    loyaltyEnabled,
    loyaltyUser,
    loyaltyLoading,
    refetchLoyaltyUser,
  } = useLoyalty();
  const loyaltyUserId = loyaltyUser?.id;
  const prevLoyaltyUserId = usePrevious(loyaltyUserId);
  const loyaltyUserPoints = loyaltyUser?.points ?? 0;

  const loyaltyRewards = useLoyaltyRewards(loyaltyUser);
  const loyaltyOffers = useLoyaltyOffers();
  const loyaltyNotifications = useLoyaltyNotifications(
    loyaltyUser,
    loyaltyOffers.loyaltyOffersEnabled
  );
  const dispatch = useAppDispatch();

  useEffectOnUpdates(() => {
    if (!loyaltyEnabled) {
      dispatch(actions.loyalty.resetLoyaltyRewardsState(loyaltyUserPoints));
    }
  }, [dispatch, loyaltyEnabled, loyaltyUserPoints]);

  // Reset rewards on loyalty user change
  useEffect(() => {
    if (prevLoyaltyUserId && !loyaltyLoading && prevLoyaltyUserId !== loyaltyUserId) {
      dispatch(actions.loyalty.resetLoyaltyRewardsState(loyaltyUserPoints));
    }
  }, [dispatch, loyaltyLoading, loyaltyUserId, loyaltyUserPoints, prevLoyaltyUserId]);

  return (
    <LoyaltyContext.Provider
      value={{
        // useLoyalty base hook
        claimPointsEnabled,
        loyaltyEnabled,
        loyaltyLoading,
        loyaltyUser,
        refetchLoyaltyUser,
        // other loyalty hooks
        ...loyaltyNotifications,
        ...loyaltyOffers,
        ...loyaltyRewards,
      }}
    >
      <InRestaurantRedemptionProvider>
        {/* To be deprecated and replaced with generic LoyaltyContext after api migration is complete */}
        <ThLoyaltyProvider>{props.children}</ThLoyaltyProvider>
      </InRestaurantRedemptionProvider>
    </LoyaltyContext.Provider>
  );
};
